<script setup>
import { RouterLink } from "vue-router";
import GlDisclosureDropdown from "~/components/gitlab/GlDisclosureDropdown.vue";
import GlIcon from "~/components/gitlab/GlIcon.vue";
import GlToggle from "~/components/gitlab/GlToggle.vue";
import { fileIconHref, iconHref } from "~/utils/icons.js";

const STATUS_ICONS = {
  failed: { color: "var(--gl-status-danger-icon-color)", name: "status-failed" },
  never: { color: "var(--gl-status-neutral-icon-color)", name: "status-neutral" },
  running: { color: "var(--gl-status-info-icon-color)", name: "status-running" },
  success: { color: "var(--gl-status-success-icon-color)", name: "status-success" },
  warning: { color: "var(--gl-status-warning-icon-color)", name: "status-alert" }
};

const ECOSYSTEM_ICONS = {
  bazel: "file",
  bun: "javascript",
  bundler: "ruby",
  cargo: "rust",
  composer: "json",
  conda: "python",
  devcontainers: "docker",
  docker: "docker",
  "docker-compose": "docker",
  "dotnet-sdk": "csharp",
  elm: "elm",
  gitsubmodule: "git",
  gomod: "go",
  gradle: "gradle",
  julia: "julia",
  maven: "java",
  mix: "elixir",
  nix: "nix",
  npm: "npm",
  nuget: "csharp",
  opentofu: "terraform",
  pip: "python",
  "pre-commit": "file",
  "rust-toolchain": "rust",
  swift: "swift",
  terraform: "terraform",
  uv: "python",
  vcpkg: "cpp"
};

function statusIcon(status) {
  return STATUS_ICONS[status] ?? STATUS_ICONS.never;
}

const { compact, isAdmin, job, projectEnabled, projectId, projectName } = defineProps({
  compact: { default: false, type: Boolean },
  isAdmin: { default: false, type: Boolean },
  job: { required: true, type: Object },
  projectEnabled: { default: true, type: Boolean },
  projectId: { default: null, type: [String, Number] },
  projectName: { default: null, type: String }
});

const emit = defineEmits(["toggle", "execute"]);

function dependencyFilesDropdownItems(job) {
  return [
    {
      items: (job.dependencyFiles ?? []).map((file) => ({ text: file })),
      name: "Monitored dependency files"
    }
  ];
}

function ecosystemIcon(ecosystem) {
  return ECOSYSTEM_ICONS[ecosystem] ?? "file";
}

function runsRoute(job) {
  return { name: "jobRuns", params: { jobId: job.id, projectId } };
}
</script>

<template>
  <li
    class="flex items-center gap-4 border-b border-[var(--gl-border-color-default)] px-5 py-4 last:border-b-0"
    data-testid="job-entry"
    :data-job-key="`${job.packageEcosystem}-${(job.directories ?? []).join(',')}`"
  >
    <!-- Job info -->
    <div class="min-w-0 flex-1">
      <div class="flex items-center gap-2">
        <svg :class="['h-4 w-4 shrink-0', (!job.enabled || !projectEnabled) && 'opacity-50']" aria-hidden="true">
          <use :href="fileIconHref(ecosystemIcon(job.packageEcosystem))" />
        </svg>
        <h2
          :class="[
            'text-sm font-semibold leading-snug text-[var(--gl-text-color-default)]',
            (!job.enabled || !projectEnabled) && 'opacity-50'
          ]"
        >
          {{ job.dependencyFiles?.[0] ?? job.packageEcosystem }}
        </h2>
        <GlDisclosureDropdown
          v-if="job.dependencyFiles?.length > 0"
          :items="dependencyFilesDropdownItems(job)"
          icon="ellipsis_h"
          no-caret
          category="tertiary"
          toggle-text="Show monitored files"
          text-sr-only
          fluid-width
          :toggle-class="(!job.enabled || !projectEnabled) && 'opacity-50'"
        />
      </div>
      <p :class="['text-xs text-[var(--gl-text-color-subtle)]', (!job.enabled || !projectEnabled) && 'opacity-50']">
        <span>{{ job.cron }}</span>
        <template v-if="job.nextRunAt">
          <span class="mx-1.5 text-[var(--gl-text-color-disabled)]">|</span>
          <span>next run {{ job.nextRunAt }}</span>
        </template>
        <template v-if="!compact && projectId">
          <span class="mx-1.5 text-[var(--gl-text-color-disabled)]">|</span>
          <RouterLink :to="runsRoute(job)" class="text-[var(--gl-text-color-link)] hover:underline"
            >Recent runs</RouterLink
          >
        </template>
      </p>
    </div>

    <!-- Right side: status + meta + actions -->
    <div class="flex shrink-0 items-center gap-4">
      <div :class="['flex items-center gap-4', (!job.enabled || !projectEnabled) && 'opacity-50']">
        <!-- Last run status + time -->
        <span
          v-if="!compact"
          class="flex items-center gap-1.5 text-xs text-[var(--gl-text-color-subtle)]"
          :title="job.lastExecuted ?? 'Never run'"
        >
          <GlIcon
            :name="statusIcon(job.lastRunStatus ?? 'never').name"
            :size="16"
            :style="{ color: statusIcon(job.lastRunStatus ?? 'never').color }"
          />
          <RouterLink
            v-if="job.lastExecuted && job.lastRunId !== null"
            :to="{ name: 'run', params: { projectId, jobId: job.id, runId: job.lastRunId } }"
            class="text-[var(--gl-text-color-link)] hover:underline"
            >{{ job.lastExecuted }}</RouterLink
          >
          <span v-else-if="job.lastExecuted">{{ job.lastExecuted }}</span>
          <span v-else class="italic text-[var(--gl-text-color-disabled)]">never</span>
        </span>

        <!-- Open MRs -->
        <span class="flex items-center gap-1 text-xs text-[var(--gl-text-color-subtle)]" title="Open merge requests">
          <svg class="h-3.5 w-3.5" aria-hidden="true">
            <use :href="iconHref('merge-request')" />
          </svg>
          <span class="font-medium text-[var(--gl-text-color-default)]">{{ job.openMrs }}</span>
          {{ job.openMrs === 1 ? "MR" : "MRs" }}
        </span>
      </div>

      <!-- Actions -->
      <div class="flex items-center gap-3 border-l border-[var(--gl-border-color-default)] pl-4">
        <button
          type="button"
          data-testid="execute-job-button"
          title="Execute job"
          :disabled="!job.enabled || !projectEnabled || job.lastRunStatus === 'running' || !isAdmin"
          class="flex h-7 w-7 items-center justify-center rounded-[var(--gl-action-border-radius)] border border-[var(--gl-text-color-default)] text-[var(--gl-text-color-default)] hover:bg-[var(--gl-background-color-subtle)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--gl-focus-ring-outer-color)] disabled:cursor-not-allowed disabled:opacity-50"
          @click="emit('execute', job)"
        >
          <GlIcon name="play" :size="14" />
        </button>
        <span class="border-l border-[var(--gl-border-color-default)] self-stretch"></span>
        <div class="leading-[0]" title="Enable/disable dependency updates">
          <GlToggle
            :model-value="job.enabled"
            :disabled="!isAdmin || !projectEnabled"
            label="Enabled"
            label-position="hidden"
            @update:model-value="emit('toggle', job, $event)"
          />
        </div>
      </div>
    </div>
  </li>
</template>
